// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import { Divider, Form } from "antd";
import { QuestionBlock } from "./QuestionBlock";
import { SurveyAddQuestionButton } from "./SurveyAddQuestionButton";
import { useTranslation } from "react-i18next";
import { Question, SurveyBlocksProps } from "../../../types/SurveyTypes";

export const QuestionBlocks: React.FunctionComponent<SurveyBlocksProps> = (
  props: SurveyBlocksProps
) => {
  const { t } = useTranslation();
  const { questions, setQuestions, editMode } = props;

  const handleAddQuestion = (question: Question, index?: number) => {
    if (index !== undefined) {
      const newQuestion = [...questions];
      newQuestion.splice(index, 0, question);
      setQuestions(newQuestion);
    } else {
      setQuestions([...questions, question]);
    }
  };

  const handleUpdateQuestion = (question: Question, index: number) => {
    const newQuestion = [...questions];
    newQuestion[index] = question;
    setQuestions(newQuestion); //
  };

  const handleDeleteQuestion = (index: number) => {
    const newQuestion = [...questions];
    newQuestion.splice(index, 1);
    setQuestions(newQuestion);
  };

  const handleAscendQuestion = (index: number) => {
    const question = questions[index];
    const newQuestion = [...questions];
    newQuestion.splice(index, 1);
    newQuestion.splice(index - 1, 0, question);
    setQuestions(newQuestion);
  };

  const handleDescendQuestion = (index: number) => {
    const question = questions[index];
    const newQuestion = [...questions];
    newQuestion.splice(index, 1);
    newQuestion.splice(index + 1, 0, question);
    setQuestions(newQuestion);
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
        <div className="flex-grow-1 d-flex align-items-center">
          <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
            <h3 className="text-secondary mb-0">
              {t("survey.survey_questions")}
            </h3>
          </Divider>
        </div>
        {editMode && questions.length < 1 && (
          <div className="d-flex align-items-center divider-sm-transparent">
            <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
              <Form.Item className="mb-0">
                <SurveyAddQuestionButton
                  buttonLabel={t("survey.global_buttons.question.add")}
                  handleAddQuestion={handleAddQuestion}
                />
              </Form.Item>
            </Divider>
          </div>
        )}
      </div>
      {editMode && questions.length < 1 && (
        <div className="mb-4 text-center">{t("survey.no_blocks")}</div>
      )}
      {questions.map((question: Question, index) => (
        <React.Fragment key={`${index}_${question.id}`}>
          <Form.Item className="mb-0">
            <QuestionBlock
              indexQuestion={index}
              question={question}
              handleDeleteQuestion={handleDeleteQuestion}
              handleAddQuestion={handleAddQuestion}
              handleUpdateQuestion={handleUpdateQuestion}
              handleAscendQuestion={
                questions.length > 1 && index > 0
                  ? handleAscendQuestion
                  : undefined
              }
              handleDescendQuestion={
                questions.length > 1 && index < questions.length - 1
                  ? handleDescendQuestion
                  : undefined
              }
              editMode={editMode}
            />
          </Form.Item>
        </React.Fragment>
      ))}
      {editMode && questions.length >= 1 && (
        <div className="d-flex justify-content-center">
          <SurveyAddQuestionButton
            buttonLabel={t("survey.global_buttons.question.add")}
            handleAddQuestion={handleAddQuestion}
          />
        </div>
      )}
    </>
  );
};

QuestionBlocks.defaultProps = {
  editMode: true
};
